import { Component, OnInit } from '@angular/core';
import { AppLocalizationService } from '../../../../../services/AppLocalizationService';
import { ReportDesignerService } from '../../../services/report-designer.service';
import { EventBusService } from '../../../../../services/event-bus.service';
import { AppSettingsService } from '../../../../../services/app-settings.service';
import { ReportDesignerStorageService } from '../../../services/report-designer-storage.service';
import { LocalizationService } from '../../../../app-settings/services/localization-service';
import { LocalizationNavigationService } from '../../../../app-settings/services/localization-navigation-service';
import { WidgetLockerService } from '../../../services/WidgetLockerService';

/**
 * Load template dialog component
 */
@Component({
  selector: 'load-template-dialog',
  templateUrl: './load-template-dialog.component.html'
})
export class LoadTemplateDialogComponent implements OnInit {

  /**
   * Indicator is the dialog openet
   */
  public isDialogOpened = false;

  /**
   * Selected language id
   */
  public selectedLanguageId: any;

  /**
   * Available languages list
   */
  public availableLanguagesDataSource: any[] = [];

  /**
   * Available templates list depending on selected language id
   */
  public availableTemplatesDataSource: any[] = [];

  /**
   * Selected template id
   */
  public selectedTemplateOid: any;

  /**
   * All available templates
   */
  public availableTemplates: any[] = [];

  /**
   * Active document
   */
  public document: any;

  /**
   * Indicatior is load enabled
   */
  public enableLoad = false;

  /**
   * Constructor
   * @param eventBus
   * @param _reportService
   * @param _appLocalization
   * @param appSettings
   * @param rdStorage
   * @param _localizationService
   * @param _localizationNavigationService
   * @param widgetLocker
   */
  constructor(private eventBus: EventBusService,
    private _reportService: ReportDesignerService,
    private _appLocalization: AppLocalizationService,
    private appSettings: AppSettingsService,
    private rdStorage: ReportDesignerStorageService,
    private _localizationService: LocalizationService,
    private _localizationNavigationService: LocalizationNavigationService,
    private widgetLocker: WidgetLockerService) { }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.eventBus.subscribeEvent('loadTemplate', (data, _options) => {
      //prevent multiple calls to open dialog
      if (!this.isDialogOpened) {
        this.isDialogOpened = true;
        this.handleOpenDialogEvent(data);
      }
    });
  }

  /**
   * Handle open dialog event
   */
  handleOpenDialogEvent(data) {
    this.document = data.document;
    this.enableLoad = false;
    this.rdStorage.setReport(0);
    this.rdStorage.setAppWidgetHeaderMode(false);
    this.mappingAvailableLanguages(data);
    this.getTemplatesWithoutJson();
  }

  /**
   * Get all templates without json
   */
  getTemplatesWithoutJson() {
    this._reportService.templateWithoutJson().subscribe(data => {
      if (data) {
        this.mappingAvailableTemplates(data);
        this.availableTemplatesDataSource = [];
        this.selectedLanguageId = 0;
        this.selectedTemplateOid = 0;
      }
    });
  }

  /**
   * Mapping available templates
   */
  mappingAvailableTemplates(data: any) {
    this.availableTemplates.push({
      Oid: 0,
      Name: "",
      templateLanguages: []
    })
    this.availableTemplates = this.availableTemplates.concat(data.filter(item => item.InvisibleFlag != 1));
  }

  /**
   * Mapping available languages
   */
  mappingAvailableLanguages(data: any) {
    this.availableLanguagesDataSource.push({
      id: 0,
      description: ""
    })
    this.availableLanguagesDataSource = this.availableLanguagesDataSource.concat(data.allReportDesignerLocales);
  }

  /**
   * Reset all data to default values
   */
  reset() {
    this.document = null;
    this.enableLoad = false;
    this.isDialogOpened = false;
    this.selectedLanguageId = null;
    this.availableLanguagesDataSource = [];
    this.availableTemplates = [];
    this.availableTemplatesDataSource = [];
    this.selectedLanguageId = null;
    this.selectedTemplateOid = null;
  }

  /**
   * On cancel action
   */
  cancel() {
    this.reset();
  }

  /**
   * On load template action
   */
  loadTemplate() {
    if (!this.validateLoadTemplate()) return;
    this.widgetLocker.reload();
    const templateId = this.selectedTemplateOid;
    const template = this.availableTemplates.find(t => t.Oid === templateId);
    const languageIds = template.templateLanguages;
    const templateLanguagesConfigurations = this.appSettings.allReportDesignerLocales.filter(l => languageIds.indexOf(l.id) !== -1);
    const firstTemplateLanguageId = this.selectedLanguageId;
    this._localizationService.getLocalization(this.selectedLanguageId).subscribe(data => {
      if (data) {
        this._localizationNavigationService.setActiveLocalization(data);
      }
    })

    this._reportService.singleTemplate(templateId).subscribe(data => {
      if (data) {
        const documentJson = JSON.parse(data.Json);
        this.document.clear();
        this._appLocalization.setReportDesignerDictionaryConfigurations(templateLanguagesConfigurations, false);
        this._appLocalization.setReportDesignerDictionaryConfigById(firstTemplateLanguageId).then(() => {
          this.rdStorage.setTemplate(data);
          //inform some change occured, so app can react (f.e. adjust dropdown languages in RD)
          this.eventBus.broadcastEvent('rd:dictionaryConfigIdSet');

          setTimeout(() => {
            this.document.loadDocument(documentJson);
            this.cancel();
            this.eventBus.broadcastEvent("templateLoaded");
            this.eventBus.broadcastEvent("enableButtonsForLoadDocument");
            this.eventBus.broadcastEvent("openConfirmDialog", {
              success: () => {
                //Nothing
              }
            }, 'templateModeDialog');
          }, 0);
        });
      }
    })
  }

  /**
   * Ask the user: Really load the template?
   * @returns
   */
  validateLoadTemplate() {
    if (!this.selectedTemplateOid) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'selectTemplate');
      return false;
    }

    return true;
  }

  /**
   * On template changed
   */
  templateChanged(): void {
    this.enableLoad = this.selectedTemplateOid > 0
  }

  /**
   * On language changed
   */
  languageChanged(): void {
    const selectedLanguageId = this.selectedLanguageId;
    const filteredTemplates = this.availableTemplates.filter(t => t.templateLanguages.indexOf(selectedLanguageId) !== -1);

    this.availableTemplatesDataSource = filteredTemplates;
    this.selectedTemplateOid = null;
    this.enableLoad = false;
  }
}
